<template>
	<RadioField
		name="interval"
		id="payment-form-interval"
		:label="$t('membership_form_payment_interval_title')"
		v-model="interval"
		:options="paymentIntervalsAsOptions"
		:disabled="[]"
		:show-error="!intervalIsValid"
		:error-message="$t('membership_form_interval_error')"
		:is-max-width-field="true"
	/>

	<AmountField
		v-model="fee"
		:label="getAmountTitle"
		:payment-amounts="paymentAmounts"
		:error-message="feeErrorMessage"
		:show-error="!feeIsValid"
		:minimum-amount="minimumAmount"
		:minimum-amount-message="$t('membership_form_payment_amount_description')"
	>
		<template #message>
			{{ $t('membership_form_payment_amount_cap_notice') }}
		</template>
	</AmountField>

	<template v-if="paymentTypes.length > 1">
		<RadioField
			name="paymentType"
			id="payment-form-type"
			:label="$t('donation_form_payment_type_title')"
			v-model="paymentType"
			:options="paymentTypesAsOptions"
			:disabled="[]"
			:show-error="!paymentTypeIsValid"
			:error-message="$t('membership_form_payment_type_error')"
			:is-max-width-field="true"
		/>
	</template>

	<IbanFields v-if="paymentType === 'BEZ'"/>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { usePaymentFieldModel } from '@src/components/pages/membership_form/usePaymentFieldModel';
import RadioField from '@src/components/shared/form_fields/RadioField.vue';
import type { CheckboxFormOption } from '@src/components/shared/form_fields/FormOptions';
import AmountField from '@src/components/shared/form_fields/AmountField.vue';
import { FeeValidity } from '@src/view_models/MembershipFee';
import IbanFields from '@src/components/shared/IbanFields.vue';

interface Props {
	validateFeeUrl: string;
	paymentAmounts: number[];
	paymentIntervals: number[];
	paymentTypes: string[];
	validateBankDataUrl: string;
	validateLegacyBankDataUrl: string;
}

const props = defineProps<Props>();
const store = useStore();
const { t } = useI18n();

const fee = usePaymentFieldModel( store, 'fee', 'setFee', props.validateFeeUrl );
const interval = usePaymentFieldModel( store, 'interval', 'setInterval', props.validateFeeUrl );
const paymentType = usePaymentFieldModel( store, 'type', 'setType', props.validateFeeUrl );

const paymentTypeIsValid = computed( () => store.getters[ 'membership_fee/typeIsValid' ] );
const feeIsValid = computed( () => store.getters[ 'membership_fee/feeIsValid' ] );
const intervalIsValid = computed( () => store.getters[ 'membership_fee/intervalIsValid' ] );

const minimumAmount = computed(
	() => store.getters[ 'membership_fee/minimumAmount' ]( store.state.membership_address.addressType )
);
const getAmountTitle = computed( () => {
	if ( interval.value === '' ) {
		return t( 'membership_form_payment_amount_title' );
	}
	return t( `membership_form_payment_amount_title_interval_${interval.value}` );
} );

const paymentIntervalsAsOptions = computed<CheckboxFormOption[]>( () => {
	return props.paymentIntervals.map(
		( intervalValue: number, index: number ) => (
			{ value: intervalValue.toString(), label: t( 'donation_form_payment_interval_' + intervalValue ), id: `interval-${ index }` }
		) );
} );

const paymentTypesAsOptions = computed<CheckboxFormOption[]>( () => {
	return props.paymentTypes.map(
		( paymentTypeValue: string, index: number ) => (
			{ value: paymentTypeValue, label: t( paymentTypeValue ), id: `paymentType-${ index }` }
		) );
} );

watch( minimumAmount, async ( newMinimumAmount ) => {
	if ( fee.value < newMinimumAmount ) {
		fee.value = '';
	}
} );

const feeErrorMessage = computed<string>( () => {
	const messages: { [ key: number ]: string } = {
		[ FeeValidity.FEE_VALID ]: '',
		[ FeeValidity.FEE_TOO_LOW ]: t( 'membership_form_payment_amount_error' ),
		[ FeeValidity.FEE_TOO_HIGH ]: t( 'membership_form_payment_amount_too_high' ),
	};
	return messages[ store.getters.feeValidity ].toString();
} );

</script>
